import {
  collection,
  getDocs,
  writeBatch,
  query,
  where,
  doc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { Collections, Song, Status } from "../../types/type";

export const song삭제api = async (songId: string): Promise<void> => {
  try {
    // Batch 크기와 관련된 상수 정의
    const MAX_BATCH_SIZE = 400; // 최대 500이지만, 여유를 둡니다.

    // 배치 초기화 함수
    const createNewBatch = () => {
      return { batch: writeBatch(db), operationCount: 0 };
    };

    let { batch, operationCount } = createNewBatch();

    // 배치 커밋 및 초기화 함수
    const commitBatchIfNeeded = async () => {
      if (operationCount === MAX_BATCH_SIZE) {
        await batch.commit();
        ({ batch, operationCount } = createNewBatch());
      }
    };

    // 1. Song 도큐먼트 삭제
    const songDocRef = doc(db, Collections.SONG, songId);
    batch.delete(songDocRef);
    operationCount++;
    await commitBatchIfNeeded();

    // 2. Short 컬렉션 업데이트
    const shortCollectionRef = collection(db, Collections.SHORT);
    const shortQuery = query(
      shortCollectionRef,
      where("originalSongId", "==", songId)
    );
    const shortSnapshot = await getDocs(shortQuery);

    for (const docSnap of shortSnapshot.docs) {
      batch.update(docSnap.ref, { originalSongId: "" });
      operationCount++;
      await commitBatchIfNeeded();
    }

    // 3. User 컬렉션 업데이트
    const userCollectionRef = collection(db, Collections.USER);
    const userSnapshot = await getDocs(userCollectionRef);

    for (const docSnap of userSnapshot.docs) {
      const userData = docSnap.data();
      let hasUpdate = false;
      const updateData: any = {};

      if (userData.likeSongList && userData.likeSongList.length > 0) {
        const updatedLikeSongList = userData.likeSongList.filter(
          (song: any) => song.songId !== songId
        );
        if (updatedLikeSongList.length !== userData.likeSongList.length) {
          updateData.likeSongList = updatedLikeSongList;
          hasUpdate = true;
        }
      }

      if (userData.playlistList && userData.playlistList.length > 0) {
        const updatedPlaylistList = userData.playlistList.map(
          (playlist: any) => {
            const updatedSongList = playlist.songList.filter(
              (song: any) => song.songId !== songId
            );
            return { ...playlist, songList: updatedSongList };
          }
        );
        if (
          JSON.stringify(updatedPlaylistList) !==
          JSON.stringify(userData.playlistList)
        ) {
          updateData.playlistList = updatedPlaylistList;
          hasUpdate = true;
        }
      }

      if (hasUpdate) {
        batch.update(docSnap.ref, updateData);
        operationCount++;
        await commitBatchIfNeeded();
      }
    }

    // 4. HomebrewPlaylist 컬렉션 업데이트
    const homebrewPlaylistCollectionRef = collection(
      db,
      Collections.HOMEBREW_PLAYLIST
    );
    const homebrewPlaylistSnapshot = await getDocs(
      homebrewPlaylistCollectionRef
    );

    for (const docSnap of homebrewPlaylistSnapshot.docs) {
      const playlistData = docSnap.data();

      if (playlistData.songList && playlistData.songList.length > 0) {
        const updatedSongList = playlistData.songList.filter(
          (song: any) => song.songId !== songId
        );

        if (updatedSongList.length !== playlistData.songList.length) {
          batch.update(docSnap.ref, { songList: updatedSongList });
          operationCount++;
          await commitBatchIfNeeded();
        }
      }
    }

    // 5. songRequest 컬렉션 업데이트
    const songRequestCollectionRef = collection(db, Collections.SONG_REQUEST);
    const songRequestQuery = query(
      songRequestCollectionRef,
      where("songId", "==", songId)
    );
    const songRequestSnapshot = await getDocs(songRequestQuery);

    for (const docSnap of songRequestSnapshot.docs) {
      batch.update(docSnap.ref, { songId: "", status: Status.FAILED });
      operationCount++;
      await commitBatchIfNeeded();
    }

    // 6. Artist 컬렉션 업데이트
    const artistCollectionRef = collection(db, Collections.ARTIST);
    const artistQuery = query(
      artistCollectionRef,
      where("songIdList", "array-contains", songId)
    );
    const artistSnapshot = await getDocs(artistQuery);

    for (const docSnap of artistSnapshot.docs) {
      const artistData = docSnap.data();
      const updatedSongIdList = artistData.songIdList.filter(
        (id: string) => id !== songId
      );

      batch.update(docSnap.ref, { songIdList: updatedSongIdList });
      operationCount++;
      await commitBatchIfNeeded();
    }

    // 남은 배치 커밋
    if (operationCount > 0) {
      await batch.commit();
    }

    console.log(
      `[실행성공] Song 도큐먼트 및 관련 참조들이 성공적으로 삭제/업데이트 되었습니다.`
    );
  } catch (error) {
    console.error("[에러] Song 도큐먼트 삭제 중 오류 발생: ", error);
  }
};

export const song수정api = async (
  songId: string,
  updatedData: Partial<Song>
): Promise<void> => {
  try {
    const MAX_BATCH_SIZE = 400;

    const createNewBatch = () => {
      return { batch: writeBatch(db), operationCount: 0 };
    };

    let { batch, operationCount } = createNewBatch();

    const commitBatchIfNeeded = async () => {
      if (operationCount === MAX_BATCH_SIZE) {
        await batch.commit();
        ({ batch, operationCount } = createNewBatch());
      }
    };

    // 1. Song 도큐먼트 업데이트
    const songDocRef = doc(db, Collections.SONG, songId);
    await updateDoc(songDocRef, updatedData);

    // 2. Short 컬렉션 업데이트
    const shortCollectionRef = collection(db, Collections.SHORT);
    const shortQuery = query(
      shortCollectionRef,
      where("originalSongId", "==", songId)
    );
    const shortSnapshot = await getDocs(shortQuery);

    for (const docSnap of shortSnapshot.docs) {
      batch.update(docSnap.ref, {
        originalSongTitle:
          updatedData.title || docSnap.data().originalSongTitle,
        originalArtistName:
          updatedData.artistName || docSnap.data().originalArtistName,
      });
      operationCount++;
      await commitBatchIfNeeded();
    }

    // 3. User 컬렉션 업데이트
    const userCollectionRef = collection(db, Collections.USER);
    const userSnapshot = await getDocs(userCollectionRef);

    for (const docSnap of userSnapshot.docs) {
      const userData = docSnap.data();
      let hasUpdate = false;
      const updateData: any = {};

      if (userData.likeSongList && userData.likeSongList.length > 0) {
        const updatedLikeSongList = userData.likeSongList.map((song: any) => {
          if (song.songId === songId) {
            hasUpdate = true;
            return { ...song, ...updatedData };
          }
          return song;
        });
        if (hasUpdate) {
          updateData.likeSongList = updatedLikeSongList;
        }
      }

      if (userData.playlistList && userData.playlistList.length > 0) {
        const updatedPlaylistList = userData.playlistList.map(
          (playlist: any) => {
            const updatedSongList = playlist.songList.map((song: any) => {
              if (song.songId === songId) {
                hasUpdate = true;
                return { ...song, ...updatedData };
              }
              return song;
            });
            return { ...playlist, songList: updatedSongList };
          }
        );
        if (hasUpdate) {
          updateData.playlistList = updatedPlaylistList;
        }
      }

      if (hasUpdate) {
        batch.update(docSnap.ref, updateData);
        operationCount++;
        await commitBatchIfNeeded();
      }
    }

    // 4. HomebrewPlaylist 컬렉션 업데이트
    const homebrewPlaylistCollectionRef = collection(
      db,
      Collections.HOMEBREW_PLAYLIST
    );
    const homebrewPlaylistSnapshot = await getDocs(
      homebrewPlaylistCollectionRef
    );

    for (const docSnap of homebrewPlaylistSnapshot.docs) {
      const playlistData = docSnap.data();
      let hasUpdate = false;

      if (playlistData.songList && playlistData.songList.length > 0) {
        const updatedSongList = playlistData.songList.map((song: any) => {
          if (song.songId === songId) {
            hasUpdate = true;
            return { ...song, ...updatedData };
          }
          return song;
        });

        if (hasUpdate) {
          batch.update(docSnap.ref, { songList: updatedSongList });
          operationCount++;
          await commitBatchIfNeeded();
        }
      }
    }

    // 5. songRequest 컬렉션 업데이트
    const songRequestCollectionRef = collection(db, Collections.SONG_REQUEST);
    const songRequestQuery = query(
      songRequestCollectionRef,
      where("songId", "==", songId)
    );
    const songRequestSnapshot = await getDocs(songRequestQuery);

    for (const docSnap of songRequestSnapshot.docs) {
      batch.update(docSnap.ref, {
        songTitle: updatedData.title || docSnap.data().songTitle,
        artistName: updatedData.artistName || docSnap.data().artistName,
      });
      operationCount++;
      await commitBatchIfNeeded();
    }

    // 6. Artist 컬렉션 업데이트
    const artistCollectionRef = collection(db, Collections.ARTIST);
    const artistQuery = query(
      artistCollectionRef,
      where("songIdList", "array-contains", songId)
    );
    const artistSnapshot = await getDocs(artistQuery);

    for (const docSnap of artistSnapshot.docs) {
      const artistData = docSnap.data();
      let hasUpdate = false;
      const updateData: any = {};

      if (artistData.songList && artistData.songList.length > 0) {
        const updatedSongList = artistData.songList.map((song: any) => {
          if (song.songId === songId) {
            hasUpdate = true;
            return { ...song, ...updatedData };
          }
          return song;
        });

        if (hasUpdate) {
          updateData.songList = updatedSongList;
          batch.update(docSnap.ref, updateData);
          operationCount++;
          await commitBatchIfNeeded();
        }
      }
    }

    // 남은 배치 커밋
    if (operationCount > 0) {
      await batch.commit();
    }

    console.log(
      `[실행성공] Song 도큐먼트 및 관련 참조들이 성공적으로 업데이트되었습니다.`
    );
  } catch (error) {
    console.error("[에러] Song 도큐먼트 업데이트 중 오류 발생: ", error);
    throw error;
  }
};
